import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export interface ExamSchedule {
  id: number;
  serialNumber: number;
  subjectCode: string;
  subjectName: string;
  day: string;
  examDate: string;
  startTime: string;
  endTime: string;
  admitCardId: number;
  createdAt: string;
  updatedAt: string;
  examName: string;
  // classId: number; // Removed classId from exam schedule
  subjectId: number;
  examType: string;
  roomNumber: string;
  instructions: string;
  status: string;
  className: string;
}

export interface AdmitCard {
  id: number;
  examType: string;
  venue: string;
  instructions: string;
  startTime: string;
  endTime: string;
  issued: boolean; // Backend now sends boolean directly
  // studentId: number;
  examSchedules: ExamSchedule[];
  sessionId?: number;
  classId?: number;
}

export interface AdmitCardFormData extends Omit<AdmitCard, "id" | "issued"> {
  id?: number;
  issued: boolean; // Keep as boolean for form data
  sessionId?: number;
  classId?: number;
}

const API_URL = `${process.env.REACT_APP_API_URL}/admit-cards`;

// const getAllAdmitCards = async (classId?: number, sessionId?: number): Promise<AdmitCard[]> => {
//   if (classId && sessionId) {
//     const response = await axios.get(`${API_URL}/class/${classId}/session/${sessionId}`);
//     return response.data;
//   }
//   const response = await axios.get(API_URL);
//   return response.data;
// };
const getAllAdmitCards = async (): Promise<AdmitCard[]> => {
  const response = await axios.get(API_URL);
  return response.data;
};

  const createAdmitCard = async (data: AdmitCardFormData): Promise<AdmitCard> => {
    if (!data.sessionId) throw new Error("Session ID is required");
    
    // Send boolean directly to backend
    const response = await axios.post(API_URL, data);
    return response.data;
  };

const getAdmitCardByClassId = async (
  classId: number,
  sessionId: number
): Promise<AdmitCard[]> => {
  const response = await axios.get(`${API_URL}/class/${classId}/${sessionId}`);
  return response.data;
};

  const updateAdmitCard = async (
    id: number,
    data: AdmitCardFormData
  ): Promise<AdmitCard> => {
    if (!data.sessionId) throw new Error("Session ID is required");
    
    // Send boolean directly to backend
    const response = await axios.put(`${API_URL}/${id}`, data);
    return response.data;
  };

const deleteAdmitCard = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

export function useAdmitCards(disableGeneralQuery: boolean = false) {
  const queryClient = useQueryClient();

  // const admitCardsQuery = useQuery<AdmitCard[], Error>({
  //   queryKey: ["admit-cards", classId, sessionId],
  //   queryFn: () => {
  //     if (!classId || !sessionId) return Promise.resolve([]);
  //     return getAllAdmitCards(classId, sessionId);
  //   },
  //   enabled: !!classId && !!sessionId,
  // });
  const admitCardsQuery = useQuery<AdmitCard[], Error>({
    queryKey: ["admit-cards"],
    queryFn: getAllAdmitCards,
    enabled: !disableGeneralQuery, // Disable for students who use the class/session-specific API
  });

  const createAdmitCardMutation = useMutation({
    mutationFn: createAdmitCard,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admit-cards"] }),
  });

  const updateAdmitCardMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdmitCardFormData }) =>
      updateAdmitCard(id, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admit-cards"] }),
  });

  const deleteAdmitCardMutation = useMutation({
    mutationFn: deleteAdmitCard,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admit-cards"] }),
  });

  const getAdmitCardByClassIdMutation = useMutation({
    mutationFn: ({
      classId,
      sessionId,
    }: {
      classId: number;
      sessionId: number;
    }) => getAdmitCardByClassId(classId, sessionId),
  });

  return {
    admitCardsQuery,
    createAdmitCardMutation,
    updateAdmitCardMutation,
    deleteAdmitCardMutation,
    getAdmitCardByClassIdMutation,
  };
}